/**
 * ToastManager.ts
 * Non-intrusive floating toast notifications for American Dream: Pocket Life.
 * Integrated with the modular Illustrated SVG Iconography Suite.
 */

import { EventBus } from '../core/EventBus';
import { getFoodIconSvg, renderUiGouacheIcon } from './Icons';
import { REGIONAL_FOODS } from '../content/RegionalFoodsData';

export type ToastType = 'info' | 'success' | 'warning' | 'error';

export interface ToastOptions {
  message: string;
  type?: ToastType;
  durationMs?: number;
  icon?: string;
}

export class ToastManager {
  public static readonly MAX_VISIBLE_TOASTS = 3;
  public static readonly DEFAULT_DURATION_MS = 3000;

  private static instance: ToastManager | null = null;
  private container: HTMLElement | null = null;
  private activeTimers: Map<HTMLElement, { dismissTimer: ReturnType<typeof setTimeout>; removeTimer?: ReturnType<typeof setTimeout> }> = new Map();

  private readonly onShowToast = (options: ToastOptions): void => {
    if (options && typeof options.message === 'string') {
      this.show(options.message, options.type, options.durationMs, options.icon);
    }
  };

  private readonly onPenaltyTriggered = ({ message }: { type: string; message: string }): void => {
    if (message) {
      this.show(message, 'warning', ToastManager.DEFAULT_DURATION_MS);
    }
  };

  private constructor(containerId: string = 'toast-container') {
    if (typeof document !== 'undefined') {
      this.container = document.getElementById(containerId);
    }
    this.bindEvents();
  }

  public static getInstance(containerId: string = 'toast-container'): ToastManager {
    if (!ToastManager.instance) {
      ToastManager.instance = new ToastManager(containerId);
    } else {
      ToastManager.instance.bindEvents();
    }
    return ToastManager.instance;
  }

  private bindEvents(): void {
    EventBus.on('SHOW_TOAST', this.onShowToast);
    EventBus.on('PENALTY_TRIGGERED', this.onPenaltyTriggered);
  }

  private removeToastImmediately(toastEl: HTMLElement): void {
    const timers = this.activeTimers.get(toastEl);
    if (timers) {
      clearTimeout(timers.dismissTimer);
      if (timers.removeTimer) {
        clearTimeout(timers.removeTimer);
      }
      this.activeTimers.delete(toastEl);
    }
    if (toastEl.parentNode) {
      toastEl.parentNode.removeChild(toastEl);
    }
  }

  public show(
    message: string,
    type: ToastType = 'info',
    durationMs: number = ToastManager.DEFAULT_DURATION_MS,
    customIconSvg?: string
  ): void {
    if (!this.container || !document.body.contains(this.container)) {
      this.container = document.getElementById('toast-container');
      if (!this.container) {
        console.warn('[ToastManager] #toast-container not found in DOM.');
        return;
      }
    }

    // Enforce maximum 3 visible toasts at the same time (FIFO eviction)
    while (this.container.children.length >= ToastManager.MAX_VISIBLE_TOASTS) {
      const oldestToast = this.container.firstElementChild as HTMLElement | null;
      if (!oldestToast) break;
      this.removeToastImmediately(oldestToast);
    }

    const toast = document.createElement('div');
    toast.className = `toast-item toast-${type}`;
    toast.setAttribute('role', 'status');

    let icon: string;
    if (customIconSvg) {
      icon = customIconSvg;
    } else {
      // Automatic detection for food notifications
      const matchedFood = REGIONAL_FOODS.find(
        (f) => message.includes(f.name) || message.includes(f.vietnameseName)
      );
      if (matchedFood) {
        icon = getFoodIconSvg(matchedFood.id, { size: 22 });
      } else {
        switch (type) {
          case 'success':
            icon = renderUiGouacheIcon('icon_check_seal', { size: 21, className: 'toast-gouache-icon' });
            break;
          case 'warning':
            icon = renderUiGouacheIcon('icon_energy_bolt', { size: 21, className: 'toast-gouache-icon' });
            break;
          case 'error':
            icon = renderUiGouacheIcon('icon_mood_low', { size: 21, className: 'toast-gouache-icon' });
            break;
          case 'info':
          default:
            icon = renderUiGouacheIcon('icon_atlas_compass', { size: 21, className: 'toast-gouache-icon' });
            break;
        }
      }
    }

    toast.innerHTML = `<span class="toast-icon" style="display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; flex-shrink: 0;">${icon}</span> <span class="toast-msg">${message}</span>`;
    this.container.appendChild(toast);

    const effectiveDuration = durationMs > 0 ? durationMs : ToastManager.DEFAULT_DURATION_MS;

    const dismissTimer = setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(-10px)';
      toast.style.transition = 'opacity 250ms ease, transform 250ms ease';
      const removeTimer = setTimeout(() => {
        this.removeToastImmediately(toast);
      }, 250);
      const record = this.activeTimers.get(toast);
      if (record) {
        record.removeTimer = removeTimer;
      }
    }, effectiveDuration);

    this.activeTimers.set(toast, { dismissTimer });
  }
}
